import { useState, useEffect } from "react";
import { Link, useSearch, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, X, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiRequest } from "@/lib/queryClient";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { severityDot } from "@/components/notification-bell";
import type { NotificationRow } from "@/components/notification-bell";

interface ListResp {
  items: NotificationRow[];
  nextCursor: string | null;
}

type FilterTab = "all" | "unread" | "warning" | "error";

const TABS: { id: FilterTab; label: string }[] = [
  { id: "all", label: "All" },
  { id: "unread", label: "Unread" },
  { id: "warning", label: "Warnings" },
  { id: "error", label: "Errors" },
];

function buildApiParams(filter: FilterTab, before: string | null): URLSearchParams {
  const params = new URLSearchParams({ limit: "50" });
  if (before) params.set("before", before);
  if (filter === "unread") params.set("unread", "1");
  if (filter === "warning") params.set("severity", "warning");
  if (filter === "error") params.set("severity", "error");
  return params;
}

export default function NotificationsPage() {
  const search = useSearch();
  const [, setLocation] = useLocation();

  const searchParams = new URLSearchParams(search);
  const filterFromUrl = (searchParams.get("filter") ?? "all") as FilterTab;
  const activeFilter: FilterTab = TABS.some((t) => t.id === filterFromUrl)
    ? filterFromUrl
    : "all";

  const qc = useQueryClient();
  const [before, setBefore] = useState<string | null>(null);
  const [allItems, setAllItems] = useState<NotificationRow[]>([]);
  const [hasMore, setHasMore] = useState(true);

  // Reset pagination whenever the filter changes.
  useEffect(() => {
    setBefore(null);
    setAllItems([]);
    setHasMore(true);
  }, [activeFilter]);

  const queryKey = ["/api/notifications/page", activeFilter, before];

  const { isLoading, isFetching } = useQuery<ListResp>({
    queryKey,
    queryFn: async () => {
      const params = buildApiParams(activeFilter, before);
      const res = await fetch(`/api/notifications?${params.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Failed to load notifications (${res.status})`);
      const data: ListResp = await res.json();
      setAllItems((prev) => {
        const existingIds = new Set(prev.map((n) => n.id));
        const newItems = data.items.filter((n) => !existingIds.has(n.id));
        return [...prev, ...newItems];
      });
      if (!data.nextCursor) setHasMore(false);
      return data;
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => apiRequest("POST", "/api/notifications/read-all", {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
      if (activeFilter === "unread") {
        // Unread tab: all items are now read, so the list should be empty.
        setAllItems([]);
        setHasMore(false);
      } else {
        setAllItems((prev) =>
          prev.map((n) => ({ ...n, readAt: n.readAt ?? new Date().toISOString() }))
        );
      }
    },
  });

  const markRead = useMutation({
    mutationFn: (id: string) =>
      apiRequest("POST", `/api/notifications/${id}/read`, {}),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
      if (activeFilter === "unread") {
        // Unread tab: the item no longer matches the filter, so remove it.
        setAllItems((prev) => prev.filter((n) => n.id !== id));
      } else {
        setAllItems((prev) =>
          prev.map((n) =>
            n.id === id ? { ...n, readAt: new Date().toISOString() } : n
          )
        );
      }
    },
  });

  const dismiss = useMutation({
    mutationFn: (id: string) =>
      apiRequest("POST", `/api/notifications/${id}/dismiss`, {}),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
      setAllItems((prev) => prev.filter((n) => n.id !== id));
    },
  });

  const unreadCount = allItems.filter((n) => !n.readAt).length;

  const loadMore = () => {
    const currentData = qc.getQueryData<ListResp>(queryKey);
    if (currentData?.nextCursor) {
      setBefore(currentData.nextCursor);
    }
  };

  const setFilter = (tab: FilterTab) => {
    const p = new URLSearchParams(search);
    if (tab === "all") {
      p.delete("filter");
    } else {
      p.set("filter", tab);
    }
    const qs = p.toString();
    setLocation(`/notifications${qs ? `?${qs}` : ""}`, { replace: true });
  };

  const emptyMessages: Record<FilterTab, string> = {
    all: "You're all caught up.",
    unread: "No unread notifications.",
    warning: "No warnings.",
    error: "No errors.",
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <Link href="/">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              data-testid="button-notifications-back"
              aria-label="Go back"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-muted-foreground" />
            <h1 className="text-xl font-semibold">All Notifications</h1>
          </div>
        </div>
        {unreadCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending}
            data-testid="button-notifications-mark-all-read"
          >
            <Check className="h-3.5 w-3.5 mr-1.5" />
            Mark all read
          </Button>
        )}
      </div>

      {/* Filter tabs */}
      <div
        className="flex gap-1 mb-4 p-1 bg-muted rounded-lg w-fit"
        data-testid="notifications-filter-tabs"
      >
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id)}
            data-testid={`filter-tab-${tab.id}`}
            className={cn(
              "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
              activeFilter === tab.id
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="rounded-lg border divide-y">
        {isLoading && allItems.length === 0 && (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">
            Loading…
          </div>
        )}
        {!isLoading && allItems.length === 0 && (
          <div className="px-4 py-16 text-center text-sm text-muted-foreground">
            <Bell className="h-8 w-8 mx-auto mb-3 opacity-30" />
            <p>{emptyMessages[activeFilter]}</p>
          </div>
        )}
        {allItems.map((n) => {
          const isUnread = !n.readAt;
          const inner = (
            <div
              className={cn(
                "px-4 py-3 flex gap-3 items-start group hover:bg-accent/30 transition-colors",
                isUnread && "bg-accent/10"
              )}
              data-testid={`notification-item-${n.id}`}
            >
              <span
                className={cn(
                  "mt-1.5 h-2.5 w-2.5 rounded-full shrink-0",
                  severityDot[n.severity]
                )}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <div
                  className={cn(
                    "text-sm leading-snug",
                    isUnread && "font-medium"
                  )}
                >
                  {n.title}
                </div>
                {n.body && (
                  <div className="text-sm text-muted-foreground mt-1">
                    {n.body}
                  </div>
                )}
                <div className="text-xs text-muted-foreground mt-1.5">
                  {formatDistanceToNow(new Date(n.createdAt), {
                    addSuffix: true,
                  })}
                </div>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                {isUnread && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      markRead.mutate(n.id);
                    }}
                    data-testid={`button-notification-read-${n.id}`}
                    aria-label="Mark read"
                  >
                    <Check className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    dismiss.mutate(n.id);
                  }}
                  data-testid={`button-notification-dismiss-${n.id}`}
                  aria-label="Dismiss"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          );

          if (n.linkUrl) {
            return (
              <Link
                key={n.id}
                href={n.linkUrl}
                onClick={() => {
                  if (isUnread) markRead.mutate(n.id);
                }}
              >
                {inner}
              </Link>
            );
          }
          return <div key={n.id}>{inner}</div>;
        })}
      </div>

      {hasMore && allItems.length > 0 && (
        <div className="mt-4 text-center">
          <Button
            variant="outline"
            size="sm"
            onClick={loadMore}
            disabled={isFetching}
            data-testid="button-notifications-load-more"
          >
            {isFetching ? "Loading…" : "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
}
